import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Swords, Trophy, Target, TrendingUp, Minus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Player {
  id: string;
  name: string;
  avatar: string | null;
  stats: any;
}

interface H2HStats {
  player1Wins: number;
  player2Wins: number;
  draws: number;
  player1Goals: number;
  player2Goals: number;
  totalMatches: number;
}

export const HeadToHeadWidget = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [player1Id, setPlayer1Id] = useState<string>('');
  const [player2Id, setPlayer2Id] = useState<string>('');
  const [h2hStats, setH2hStats] = useState<H2HStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPlayers = async () => {
      const { data } = await supabase
        .from('players_public')
        .select('id, name, avatar, stats')
        .eq('approval_status', 'approved')
        .order('name');
      if (data) setPlayers(data);
    };
    fetchPlayers();
  }, []);

  useEffect(() => {
    if (player1Id && player2Id && player1Id !== player2Id) {
      fetchH2HStats();
    }
  }, [player1Id, player2Id]);

  const fetchH2HStats = async () => {
    setLoading(true);
    try {
      const { data: matches } = await supabase
        .from('matches')
        .select('*')
        .eq('status', 'completed')
        .or(`and(team_a.eq.${player1Id},team_b.eq.${player2Id}),and(team_a.eq.${player2Id},team_b.eq.${player1Id})`);

      if (matches) {
        let p1Wins = 0, p2Wins = 0, draws = 0, p1Goals = 0, p2Goals = 0;
        
        matches.forEach(match => {
          const p1IsTeamA = match.team_a === player1Id;
          const p1Score = p1IsTeamA ? match.score_a : match.score_b;
          const p2Score = p1IsTeamA ? match.score_b : match.score_a;
          
          p1Goals += p1Score || 0;
          p2Goals += p2Score || 0;
          
          if (p1Score > p2Score) p1Wins++;
          else if (p2Score > p1Score) p2Wins++;
          else draws++;
        });

        setH2hStats({
          player1Wins: p1Wins,
          player2Wins: p2Wins,
          draws,
          player1Goals: p1Goals,
          player2Goals: p2Goals,
          totalMatches: matches.length
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const player1 = players.find(p => p.id === player1Id);
  const player2 = players.find(p => p.id === player2Id);

  return (
    <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
      <div className="flex items-center gap-2 mb-4">
        <Swords className="w-5 h-5 text-primary" />
        <h3 className="font-bold text-lg">Head-to-Head</h3>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Select value={player1Id} onValueChange={setPlayer1Id}>
          <SelectTrigger className="bg-background/50">
            <SelectValue placeholder="Player 1" />
          </SelectTrigger>
          <SelectContent>
            {players.filter(p => p.id !== player2Id).map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={player2Id} onValueChange={setPlayer2Id}>
          <SelectTrigger className="bg-background/50">
            <SelectValue placeholder="Player 2" />
          </SelectTrigger>
          <SelectContent>
            {players.filter(p => p.id !== player1Id).map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {player1 && player2 && h2hStats && (
        <div className="space-y-4">
          {/* Players Face-off */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center gap-2">
              <Avatar className="w-16 h-16 border-2 border-primary">
                <AvatarImage src={player1.avatar || ''} />
                <AvatarFallback>{player1.name[0]}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium truncate max-w-[80px]">{player1.name}</span>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">
                {h2hStats.player1Wins} - {h2hStats.draws} - {h2hStats.player2Wins}
              </div>
              <div className="text-xs text-muted-foreground">W - D - L</div>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Avatar className="w-16 h-16 border-2 border-yellow-500">
                <AvatarImage src={player2.avatar || ''} />
                <AvatarFallback>{player2.name[0]}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium truncate max-w-[80px]">{player2.name}</span>
            </div>
          </div>

          {/* Stats Comparison */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-primary/10 rounded-lg p-2">
              <Trophy className="w-4 h-4 mx-auto text-yellow-500 mb-1" />
              <div className="text-lg font-bold">{h2hStats.totalMatches}</div>
              <div className="text-xs text-muted-foreground">Matches</div>
            </div>
            <div className="bg-primary/10 rounded-lg p-2">
              <Target className="w-4 h-4 mx-auto text-green-500 mb-1" />
              <div className="text-lg font-bold">{h2hStats.player1Goals}</div>
              <div className="text-xs text-muted-foreground">{player1.name.split(' ')[0]} Goals</div>
            </div>
            <div className="bg-primary/10 rounded-lg p-2">
              <Target className="w-4 h-4 mx-auto text-red-500 mb-1" />
              <div className="text-lg font-bold">{h2hStats.player2Goals}</div>
              <div className="text-xs text-muted-foreground">{player2.name.split(' ')[0]} Goals</div>
            </div>
          </div>

          {/* Win Rate Bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-primary">{Math.round((h2hStats.player1Wins / h2hStats.totalMatches) * 100) || 0}%</span>
              <span className="text-muted-foreground">Win Rate</span>
              <span className="text-yellow-500">{Math.round((h2hStats.player2Wins / h2hStats.totalMatches) * 100) || 0}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden flex">
              <div 
                className="bg-primary transition-all" 
                style={{ width: `${(h2hStats.player1Wins / h2hStats.totalMatches) * 100 || 0}%` }}
              />
              <div 
                className="bg-muted-foreground transition-all" 
                style={{ width: `${(h2hStats.draws / h2hStats.totalMatches) * 100 || 0}%` }}
              />
              <div 
                className="bg-yellow-500 transition-all" 
                style={{ width: `${(h2hStats.player2Wins / h2hStats.totalMatches) * 100 || 0}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {(!player1Id || !player2Id) && (
        <div className="text-center text-muted-foreground py-8">
          দুইজন প্লেয়ার সিলেক্ট করুন
        </div>
      )}
    </Card>
  );
};
